import type { RefObject } from "react";
import axiosInstance from "../axiosInstance";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
const isDev = process.env.REACT_APP_IS_DEV === "true";

export const INT_MAX = 2147483647;

/** terminals.firmware_type UI 표시 (1: 일반, 2: AOP) */
export function formatTerminalFirmwareType(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "string" ? parseInt(value, 10) : Number(value);
  if (Number.isNaN(n)) return "-";
  if (n === 1) return "일반";
  if (n === 2) return "AOP";
  return "-";
}

const CRC16_TABLE = new Uint16Array([
  0x0000, 0x8005, 0x800f, 0x000a, 0x801b, 0x001e, 0x0014, 0x8011, 0x8033,
  0x0036, 0x003c, 0x8039, 0x0028, 0x802d, 0x8027, 0x0022, 0x8063, 0x0066,
  0x006c, 0x8069, 0x0078, 0x807d, 0x8077, 0x0072, 0x0050, 0x8055, 0x805f,
  0x005a, 0x804b, 0x004e, 0x0044, 0x8041, 0x80c3, 0x00c6, 0x00cc, 0x80c9,
  0x00d8, 0x80dd, 0x80d7, 0x00d2, 0x00f0, 0x80f5, 0x80ff, 0x00fa, 0x80eb,
  0x00ee, 0x00e4, 0x80e1, 0x00a0, 0x80a5, 0x80af, 0x00aa, 0x80bb, 0x00be,
  0x00b4, 0x80b1, 0x8093, 0x0096, 0x009c, 0x8099, 0x0088, 0x808d, 0x8087,
  0x0082, 0x8183, 0x0186, 0x018c, 0x8189, 0x0198, 0x819d, 0x8197, 0x0192,
  0x01b0, 0x81b5, 0x81bf, 0x01ba, 0x81ab, 0x01ae, 0x01a4, 0x81a1, 0x01e0,
  0x81e5, 0x81ef, 0x01ea, 0x81fb, 0x01fe, 0x01f4, 0x81f1, 0x81d3, 0x01d6,
  0x01dc, 0x81d9, 0x01c8, 0x81cd, 0x81c7, 0x01c2, 0x0140, 0x8145, 0x814f,
  0x014a, 0x815b, 0x015e, 0x0154, 0x8151, 0x8173, 0x0176, 0x017c, 0x8179,
  0x0168, 0x816d, 0x8167, 0x0162, 0x8123, 0x0126, 0x012c, 0x8129, 0x0138,
  0x813d, 0x8137, 0x0132, 0x0110, 0x8115, 0x811f, 0x011a, 0x810b, 0x010e,
  0x0104, 0x8101, 0x8303, 0x0306, 0x030c, 0x8309, 0x0318, 0x831d, 0x8317,
  0x0312, 0x0330, 0x8335, 0x833f, 0x033a, 0x832b, 0x032e, 0x0324, 0x8321,
  0x0360, 0x8365, 0x836f, 0x036a, 0x837b, 0x037e, 0x0374, 0x8371, 0x8353,
  0x0356, 0x035c, 0x8359, 0x0348, 0x834d, 0x8347, 0x0342, 0x03c0, 0x83c5,
  0x83cf, 0x03ca, 0x83db, 0x03de, 0x03d4, 0x83d1, 0x83f3, 0x03f6, 0x03fc,
  0x83f9, 0x03e8, 0x83ed, 0x83e7, 0x03e2, 0x83a3, 0x03a6, 0x03ac, 0x83a9,
  0x03b8, 0x83bd, 0x83b7, 0x03b2, 0x0390, 0x8395, 0x839f, 0x039a, 0x838b,
  0x038e, 0x0384, 0x8381, 0x0280, 0x8285, 0x828f, 0x028a, 0x829b, 0x029e,
  0x0294, 0x8291, 0x82b3, 0x02b6, 0x02bc, 0x82b9, 0x02a8, 0x82ad, 0x82a7,
  0x02a2, 0x82e3, 0x02e6, 0x02ec, 0x82e9, 0x02f8, 0x82fd, 0x82f7, 0x02f2,
  0x02d0, 0x82d5, 0x82df, 0x02da, 0x82cb, 0x02ce, 0x02c4, 0x82c1, 0x8243,
  0x0246, 0x024c, 0x8249, 0x0258, 0x825d, 0x8257, 0x0252, 0x0270, 0x8275,
  0x827f, 0x027a, 0x826b, 0x026e, 0x0264, 0x8261, 0x0220, 0x8225, 0x822f,
  0x022a, 0x823b, 0x023e, 0x0234, 0x8231, 0x8213, 0x0216, 0x021c, 0x8219,
  0x0208, 0x820d, 0x8207, 0x0202,
]);

export const calcCrc16 = (data: number[]): number => {
  let crc = 0;
  for (let i = 0; i < data.length; i++) {
    const index = ((crc >> 8) ^ data[i]) & 0xff;
    crc = ((crc << 8) ^ CRC16_TABLE[index]) & 0xffff;
  }
  return crc;
};

let msgSeqNo = 0;
export async function sendBlockJs(
  writer: WritableStreamDefaultWriter<Uint8Array>,
  sendBytes: Uint8Array,
  mainCmd: number,
  subCmd: number,
  onSent?: (packet: Uint8Array) => void
) {
  const FIXED_LENGTH = sendBytes.length + 1;

  const data = new Uint8Array(FIXED_LENGTH);
  data[0] = subCmd;
  data.set(sendBytes, 1); // insert from index 1
  // rest already zero-padded because new Uint8Array initializes to 0

  // 2. Build full message
  const OutMesg = new Uint8Array(4096);
  const TempMesg = new Uint8Array(4096);

  let iIdx = 0;
  OutMesg[iIdx++] = 0x02; // STX
  OutMesg[iIdx++] = msgSeqNo++ & 0xff; // Seq No
  OutMesg[iIdx++] = 0x0b; // Sender Index H
  OutMesg[iIdx++] = 0x01; // Sender Index L
  OutMesg[iIdx++] = 0x0e; // Receiver Index H
  OutMesg[iIdx++] = 0x01; // Receiver Index L
  OutMesg[iIdx++] = mainCmd; // Instruction Code

  OutMesg[iIdx++] = (FIXED_LENGTH >> 8) & 0xff; // Message Length H (0x08)
  OutMesg[iIdx++] = FIXED_LENGTH & 0xff; // Message Length L (0x01)

  // insert 2049 bytes of data
  OutMesg.set(data, iIdx);
  iIdx += FIXED_LENGTH;

  // 3. Copy for CRC16 from OutMesg[1] ~ [8+Length]
  for (let i = 0; i < 8 + FIXED_LENGTH; i++) {
    TempMesg[i] = OutMesg[1 + i];
  }

  // CRC16 using your lookup-based function
  const crc16 = calcCrc16(Array.from(TempMesg.slice(0, 8 + FIXED_LENGTH)));

  OutMesg[iIdx++] = (crc16 >> 8) & 0xff;
  OutMesg[iIdx++] = crc16 & 0xff;
  OutMesg[iIdx++] = 0x03; // ETX

  const finalPacket = OutMesg.slice(0, iIdx);
  if (onSent) onSent(finalPacket);
  await writer.write(finalPacket);
}

/*
export const startReading = (
  parser: (byte: number, buffer: number[]) => number[] | null,
  port: SerialPort,
  timeoutMs: number = 30000,
  serialReceiveRef: RefObject<ReadableStreamDefaultReader<Uint8Array> | null>,
  abortControllerRef: RefObject<AbortController | null>
): Promise<number[]> => {
  return new Promise(async (resolve, reject) => {
    const reader = port.readable?.getReader();
    if (!reader) return reject("❌ Reader 생성 실패");

    serialReceiveRef.current = reader;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let recvBuffer: number[] = [];
    let resolved = false;

    const timeout = setTimeout(() => {
      if (!resolved) {
        abortController.abort();
        reader.releaseLock();
        reject("단말기 응답 없음 (Timeout)");
      }
    }, timeoutMs);

    try {
      while (!resolved) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        console.log(value);
        for (const byte of Array.from(value)) {
          const result = parser(byte, recvBuffer);
          if (result) {
            resolved = true;
            clearTimeout(timeout);
            reader.releaseLock();
            resolve(result);
            break;
          }

          // 너무 길면 초기화
          if (recvBuffer.length > 4096) {
            console.warn("⚠️ 비정상적 수신 길이 → 초기화");
            recvBuffer = [];
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("❌ 수신 중 오류:", err);
        reject(err);
      }
    } finally {
      reader.releaseLock();
    }
  });
};
*/

const STX = 0x02;
const ETX = 0x03;

interface Parser {
  (byte: number, buffer: number[]): number[] | null;
  reset: () => void;
}

export const startReading = (
  parser: Parser,
  port: SerialPort,
  timeoutMs: number = 30000,
  serialReceiveRef: RefObject<ReadableStreamDefaultReader<Uint8Array> | null>,
  abortControllerRef: RefObject<AbortController | null>
): Promise<number[]> => {
  return new Promise(async (resolve, reject) => {
    const reader = port.readable?.getReader();
    if (!reader) return reject("❌ Reader 생성 실패");

    serialReceiveRef.current = reader;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let recvBuffer: number[] = [];
    let resolved = false;

    const timeout = setTimeout(() => {
      if (!resolved) {
        abortController.abort();
        reader.releaseLock();
        parser.reset?.(); // ✅ 파서 상태 초기화
        reject("단말기 응답 없음 (Timeout)");
      }
    }, timeoutMs);

    try {
      while (!resolved) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        for (const byte of Array.from(value)) {
          const result = parser(byte, recvBuffer);
          if (result) {
            resolved = true;
            clearTimeout(timeout);
            reader.releaseLock();
            resolve(result);
            break;
          }

          if (recvBuffer.length > 4096) {
            console.warn("⚠️ 비정상적 수신 길이 → 초기화");
            recvBuffer = [];
            parser.reset?.();
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("❌ 수신 중 오류:", err);
        parser.reset?.(); // ✅ 오류 시에도 초기화
        reject(err);
      }
    } finally {
      parser.reset?.();
      reader.releaseLock();
    }
  });
};

/** 포트 연결 후 단말기→웹 수신 raw hex 디버그용 (주입 시에는 stop 후 startReading 사용) */
export const stopSerialRxMonitor = async (
  monitorReaderRef: RefObject<ReadableStreamDefaultReader<Uint8Array> | null>
): Promise<void> => {
  const reader = monitorReaderRef.current;
  if (!reader) return;
  monitorReaderRef.current = null;
  try {
    await reader.cancel();
  } catch {
    /* ignore */
  }
  try {
    reader.releaseLock();
  } catch {
    /* ignore */
  }
};

/** CAT/Android-POS: STX | CMD | LEN(ASCII 2) | DATA | ETX | LRC */
export const buildStxEtxAsciiLenPacket = (
  cmd: number,
  payload: string
): Uint8Array => {
  const data = new TextEncoder().encode(payload);
  const len = data.length;
  const lenStr = len.toString().padStart(2, "0");
  const arr = new Uint8Array(6 + len);
  let idx = 0;
  arr[idx++] = 0x02;
  arr[idx++] = cmd;
  arr[idx++] = lenStr.charCodeAt(0);
  arr[idx++] = lenStr.charCodeAt(1);
  arr.set(data, idx);
  idx += len;
  arr[idx++] = 0x03;
  arr[idx] = xorChecksumByte(arr);
  return arr;
};

/** A1 대기 프레임: `02 A1 03 LRC`(4B) 또는 `A1 03 LRC`(3B, STX 생략 단말) */
export const drainA1Frames = (
  buffer: number[],
): { frames: number[][]; remaining: number[] } => {
  const frames: number[][] = [];
  let i = 0;
  while (i < buffer.length) {
    const left = buffer.length - i;
    if (
      left >= 4 &&
      buffer[i] === 0x02 &&
      buffer[i + 1] === 0xa1 &&
      buffer[i + 2] === 0x03
    ) {
      frames.push(buffer.slice(i, i + 4));
      i += 4;
      continue;
    }
    if (left >= 3 && buffer[i] === 0xa1 && buffer[i + 1] === 0x03) {
      frames.push([0x02, 0xa1, 0x03, buffer[i + 2]]);
      i += 3;
      continue;
    }

    // 버퍼 맨 끝에 A1 프레임 후보가 잘려서 덜 도착한 경우만 대기(break)
    if (left < 4 && buffer[i] === 0x02) {
      if (
        left === 1 ||
        (left === 2 && buffer[i + 1] === 0xa1) ||
        (left === 3 && buffer[i + 1] === 0xa1 && buffer[i + 2] === 0x03)
      ) {
        break;
      }
    }
    if (left < 3 && buffer[i] === 0xa1) {
      if (left === 1 || (left === 2 && buffer[i + 1] === 0x03)) {
        break;
      }
    }

    // 쓰레기 바이트는 1바이트씩 스킵하여 슬라이딩 전진
    i += 1;
  }
  const remaining = buffer.slice(i);
  return {
    frames,
    remaining: remaining.length > 32 ? remaining.slice(-32) : remaining,
  };
};

export const startSerialRxMonitor = (
  port: SerialPort,
  onChunk: (bytes: number[], hex: string) => void,
  monitorReaderRef: RefObject<ReadableStreamDefaultReader<Uint8Array> | null>
): boolean => {
  if (!port.readable) {
    console.warn("[SerialRxMonitor] readable 없음");
    return false;
  }
  if (port.readable.locked) {
    console.warn("[SerialRxMonitor] readable 이미 사용 중");
    return false;
  }

  void (async () => {
    while (port.readable) {
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
      try {
        reader = port.readable.getReader();
        monitorReaderRef.current = reader;

        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            // reader.cancel() 또는 스트림 종료 시 정리 후 종료
            if (monitorReaderRef.current === reader) {
              monitorReaderRef.current = null;
            }
            try {
              reader.releaseLock();
            } catch {
              /* ignore */
            }
            return;
          }
          if (!value?.length) continue;
          const bytes = Array.from(value);
          const hex = bytes
            .map((b) => b.toString(16).padStart(2, "0"))
            .join(" ");
          onChunk(bytes, hex);
        }
      } catch (err) {
        const name = (err as Error)?.name;
        // 외부에서 stopSerialRxMonitor()를 호출했거나 AbortError인 경우 루프 종료
        if (monitorReaderRef.current !== reader || name === "AbortError") {
          if (monitorReaderRef.current === reader) {
            monitorReaderRef.current = null;
          }
          try {
            reader?.releaseLock();
          } catch {
            /* ignore */
          }
          return;
        }

        // 단말기 꽂을 때 튀는 접점 글리치(FramingError 등): 락 해제 후 50ms 대기하고 새 리더로 재시도
        console.warn(
          "[SerialRxMonitor] 하드웨어 글리치/수신 오류 발생 → 리더 자동 복구 재시도:",
          err
        );
        try {
          reader?.releaseLock();
        } catch {
          /* ignore */
        }
        await new Promise((r) => setTimeout(r, 50));
        continue;
      }
    }
  })();

  return true;
};

export const parseMasterKey = (): Parser => {
  let started = false;
  let expectedLength = 0;

  const parser = (byte: number, buffer: number[]): number[] | null => {
    if (!started) {
      if (byte === STX) {
        started = true;
        buffer.length = 0;
        buffer.push(byte);
        expectedLength = 0;
      }
      return null;
    }

    buffer.push(byte);

    if (buffer.length >= 9 && expectedLength === 0) {
      const lenLo = buffer[7];
      const lenHi = buffer[8];
      const dataLen = (lenLo << 8) | lenHi;
      expectedLength = dataLen + 12;
    }

    if (expectedLength > 0 && buffer.length >= expectedLength) {
      if (buffer[expectedLength - 1] !== ETX) {
        console.error("❌ ETX 누락");
        parser.reset();
        buffer.length = 0;
        return null;
      }

      const dataLen = expectedLength - 12;
      const msgForCrc = buffer.slice(1, 1 + 8 + dataLen);
      const expectedCrcHi = buffer[8 + dataLen + 1];
      const expectedCrcLo = buffer[8 + dataLen + 2];
      const crc16 = calcCrc16(msgForCrc);

      if (expectedCrcHi !== crc16 >> 8 || expectedCrcLo !== (crc16 & 0xff)) {
        console.error("❌ CRC 불일치");
        parser.reset();
        buffer.length = 0;
        return null;
      }

      const payload = buffer.slice(9, 9 + dataLen);
      buffer.splice(0, expectedLength);
      parser.reset();
      return payload;
    }

    return null;
  };

  parser.reset = () => {
    started = false;
    expectedLength = 0;
  };

  return parser;
};

export const parseSpSnType1Step1 = (): Parser => {
  let started = false;
  let expectedLength = 0;

  const parser = (byte: number, buffer: number[]): number[] | null => {
    if (!started) {
      if (byte === STX) {
        started = true;
        buffer.length = 0;
        buffer.push(byte);
        expectedLength = 0;
      }
      return null;
    }

    buffer.push(byte);

    if (buffer.length >= 4 && expectedLength === 0) {
      expectedLength = 4;
    }

    if (expectedLength > 0 && buffer.length >= expectedLength) {
      if (buffer[expectedLength - 2] !== ETX) {
        parser.reset();
        buffer.length = 0;
        return null;
      }
      const result = [...buffer];
      parser.reset();
      buffer.length = 0;
      return result;
    }

    return null;
  };

  parser.reset = () => {
    started = false;
    expectedLength = 0;
  };

  return parser;
};

export const parseSpSnType1Step2 = (): Parser => {
  const parser = (byte: number, buffer: number[]): number[] | null => {
    buffer.push(byte);

    for (let i = 0; i <= buffer.length - 6; i++) {
      if (buffer[i] !== STX || buffer[i + 1] !== 0xa3) continue;
      const hi = buffer[i + 2];
      const lo = buffer[i + 3];
      if (hi < 0x30 || hi > 0x39 || lo < 0x30 || lo > 0x39) continue;

      const len = (hi - 0x30) * 10 + (lo - 0x30);
      const total = 6 + len;
      if (i + total > buffer.length) {
        break;
      }

      if (buffer[i + 4 + len] !== ETX) {
        continue;
      }

      const frame = buffer.slice(i, i + total);
      const lrc = frame.slice(0, -1).reduce((acc, v) => acc ^ v, 0);
      if (lrc !== frame[frame.length - 1]) {
        continue;
      }

      const payload = buffer.slice(i + 4, i + 4 + len);
      buffer.length = 0;
      return payload;
    }

    if (buffer.length > 2048) {
      buffer.splice(0, buffer.length - 512);
    }

    return null;
  };

  parser.reset = () => {};

  return parser;
};

export const parseDikType1 = (): Parser => {
  const parser = (byte: number, buffer: number[]): number[] | null => {
    buffer.push(byte);

    for (let i = 0; i <= buffer.length - 5; i++) {
      if (buffer[i] !== STX) continue;
      if (i + 4 > buffer.length) break;

      const b0 = buffer[i + 2];
      const b1 = buffer[i + 3];
      const d1 = (b0 >> 4) & 0x0f;
      const d2 = b0 & 0x0f;
      const d3 = (b1 >> 4) & 0x0f;
      const d4 = b1 & 0x0f;
      if (d1 > 9 || d2 > 9 || d3 > 9 || d4 > 9) continue;

      const len = d1 * 1000 + d2 * 100 + d3 * 10 + d4;
      const total = 4 + len + 1;
      if (i + total > buffer.length) {
        break;
      }

      const frame = buffer.slice(i, i + total);
      const lrc = frame.slice(0, -1).reduce((acc, v) => acc ^ v, 0);
      if (lrc !== frame[frame.length - 1]) {
        continue;
      }

      const payload = buffer.slice(i + 4, i + 4 + len);
      buffer.length = 0;
      return payload;
    }

    if (buffer.length > 2048) {
      buffer.splice(0, buffer.length - 512);
    }

    return null;
  };

  parser.reset = () => {};

  return parser;
};

// NFC_READY 시퀀스가 "버퍼에 그대로 들어온 그대로" 반환되는 파서
export const parseNfcReady = (): Parser => {
  const target = [
    0x4e, 0x46, 0x43, 0x5f, 0x52, 0x45, 0x41, 0x44, 0x59, 0x0a, 0x0d,
  ];
  let buffer: number[] = [];

  const parser = (byte: number, _externalBuf: number[]): number[] | null => {
    buffer.push(byte);

    if (buffer.length >= target.length) {
      // 버퍼 내에서 target 시퀀스 탐색
      for (let i = 0; i <= buffer.length - target.length; i++) {
        let ok = true;
        for (let j = 0; j < target.length; j++) {
          if (buffer[i + j] !== target[j]) {
            ok = false;
            break;
          }
        }
        if (ok) {
          // 들어온 원본 버퍼에서 해당 부분 "그대로" 잘라 반환
          const result = buffer.slice(i, i + target.length);
          // 처리한 구간까지 버퍼에서 제거 (남은 데이터는 유지)
          buffer.splice(0, i + target.length);
          return result;
        }
      }
    }
    return null;
  };

  parser.reset = () => {
    buffer = [];
  };

  return parser;
};

export const delay = (ms: number) => {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
};

/*
export const parseMasterKey = (() => {
  let started = false;
  let expectedLength = 0;

  return (byte: number, buffer: number[]): number[] | null => {
    if (!started) {
      if (byte === STX) {
        console.log('STX 수신');
        started = true;
        buffer.length = 0;
        buffer.push(byte);
        expectedLength = 0;
      }
      return null;
    }
    buffer.push(byte);
    if (buffer.length >= 9 && expectedLength === 0) {
        console.log('최소length충족');
      const lenLo = buffer[7];
      const lenHi = buffer[8];
      const dataLen = (lenLo << 8) | lenHi;
      console.log('dataLen : '+dataLen);
      expectedLength = dataLen + 12;
    }

    if (expectedLength > 0 && buffer.length >= expectedLength) {
      console.log('expectedLength 만족 : '+expectedLength);
      if (buffer[expectedLength - 1] !== ETX) {
        console.error("❌ ETX 누락");
        started = false;
        buffer.length = 0;
        expectedLength = 0;
        return null;
      }

      const dataLen = expectedLength - 12;
      const msgForCrc = buffer.slice(1, 1 + 8 + dataLen);
      const expectedCrcHi = buffer[8 + dataLen + 1];
      const expectedCrcLo = buffer[8 + dataLen + 2];
      const crc16 = calcCrc16(msgForCrc);

      if (expectedCrcHi !== (crc16 >> 8) || expectedCrcLo !== (crc16 & 0xff)) {
        console.error("❌ CRC 불일치");
        started = false;
        buffer.length = 0;
        expectedLength = 0;
        return null;
      }

      const payload = buffer.slice(9, 9 + dataLen);
      buffer.splice(0, expectedLength);
      return payload;
    }

    return null;
  };
})();


export const parseSpSnType1Step1 = (() => {
  let started = false;
  let expectedLength = 0;

  return (byte: number, buffer: number[]): number[] | null => {
    if (!started) {
      if (byte === STX) {
        started = true;
        buffer.length = 0;
        buffer.push(byte);
        expectedLength = 0;
      }
      return null;
    }

    buffer.push(byte);

    if (buffer.length >= 4 && expectedLength === 0) {
      expectedLength = 4;
    }

    if (expectedLength > 0 && buffer.length >= expectedLength) {
      if (buffer[expectedLength - 2] !== ETX) {
        console.error("❌ ETX 누락");
        started = false;
        buffer.length = 0;
        expectedLength = 0;
        return null;
      }
      return [...buffer];
    }

    return null;
  };
})();

export const parseSpSnType1Step2 = (() => {
  let started = false;
  let expectedLength = 0;

  return (byte: number, buffer: number[]): number[] | null => {
    if (!started) {
      if (byte === STX) {
        started = true;
        buffer.length = 0;
        buffer.push(byte);
        expectedLength = 0;
      }
      return null;
    }

    buffer.push(byte);

    if (buffer.length === 4 && expectedLength === 0) {
      const hi = buffer[2], lo = buffer[3];
      if (hi < 0x30 || hi > 0x39 || lo < 0x30 || lo > 0x39) {
        started = false;
        buffer.length = 0;
        return null;
      }

      const len = (hi - 0x30) * 10 + (lo - 0x30);
      expectedLength = 1 + 1 + 2 + len + 1 + 1;
    }

    if (expectedLength > 0 && buffer.length === expectedLength) {
      if (buffer[expectedLength - 2] !== ETX) return null;
      const lrc = buffer.slice(0, expectedLength - 1).reduce((acc, v) => acc ^ v, 0);
      if (lrc !== buffer[expectedLength - 1]) return null;

      return buffer.slice(4, expectedLength - 2);
    }

    return null;
  };
})();

export const parseDikType1 = (() => {
  let started = false;
  let expectedLength = 0;

  return (byte: number, buffer: number[]): number[] | null => {
    if (!started) {
      if (byte === STX) {
        started = true;
        buffer.length = 0;
        buffer.push(byte);
        expectedLength = 0;
      }
      return null;
    }

    buffer.push(byte);

    if (buffer.length === 4 && expectedLength === 0) {
      const [b0, b1] = [buffer[2], buffer[3]];
      const d1 = (b0 >> 4) & 0x0F, d2 = b0 & 0x0F;
      const d3 = (b1 >> 4) & 0x0F, d4 = b1 & 0x0F;

      if (d1 > 9 || d2 > 9 || d3 > 9 || d4 > 9) {
        started = false;
        buffer.length = 0;
        return null;
      }

      const len = d1 * 1000 + d2 * 100 + d3 * 10 + d4;
      expectedLength = 1 + 1 + 2 + len + 1;
    }

    if (expectedLength > 0 && buffer.length === expectedLength) {
      const lrc = buffer.slice(0, expectedLength - 1).reduce((acc, v) => acc ^ v, 0);
      if (lrc !== buffer[expectedLength - 1]) return null;

      return buffer.slice(4, expectedLength - 1);
    }

    return null;
  };
})();
*/

/*
export const startReadingOnce = (
  port: SerialPort,
  timeoutMs: number = 10000,
  serialReceiveRef: RefObject<ReadableStreamDefaultReader<Uint8Array> | null>,
  abortControllerRef: RefObject<AbortController | null>
): Promise<number[]> => {
  return new Promise(async (resolve, reject) => {
    
    const reader = port.readable?.getReader();
    if (!reader) return reject("❌ Reader 생성 실패");

    serialReceiveRef.current = reader;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const STX = 0x02;
    const ETX = 0x03;
    const MIN_FRAME_LENGTH = 12;

    let recvBuffer: number[] = [];
    let started = false;
    let expectedLength = 0;
    let resolved = false;

    const timeout = setTimeout(() => {
      if (!resolved) {
        abortController.abort();
        reader.releaseLock();
        reject("단말기 응답 없음 (Timeout)");
      }
    }, timeoutMs);
    try {
      while (!resolved) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;

        for (const byte of Array.from(value)) {
          if (!started) {
            if (byte === STX) {
              started = true;
              recvBuffer = [STX];
              expectedLength = 0;
            }
            continue;
          }

          recvBuffer.push(byte);

          if (recvBuffer.length >= 9 && expectedLength === 0) {
            const lenHi = recvBuffer[7];
            const lenLo = recvBuffer[8];
            const dataLen = (lenHi << 8) | lenLo;
            expectedLength = dataLen + 12;
          }

          if (expectedLength > 0 && recvBuffer.length >= expectedLength) {
            const lastByte = recvBuffer[expectedLength - 1];
            if (lastByte !== ETX) {
              console.error("❌ ETX 누락 또는 잘못된 위치");
              started = false;
              recvBuffer = [];
              expectedLength = 0;
              continue;
            }

            const dataLen = expectedLength - 12;
            const msgForCrc = recvBuffer.slice(1, 1 + 8 + dataLen);
            const expectedCrcHi = recvBuffer[8 + dataLen + 1];
            const expectedCrcLo = recvBuffer[8 + dataLen + 2];
            const crc16 = calcCrc16(msgForCrc);

            if (
              expectedCrcHi !== (crc16 >> 8) ||
              expectedCrcLo !== (crc16 & 0xff)
            ) {
              console.error("❌ CRC 불일치");
              started = false;
              recvBuffer = [];
              expectedLength = 0;
              continue;
            }

            // ✅ 유효한 패킷 수신 완료
            const dataPayload = recvBuffer.slice(9, 9 + dataLen);

            resolved = true;
            clearTimeout(timeout);
            reader.releaseLock();
            resolve(dataPayload);
            break;
          }

          // 너무 긴 garbage 방지
          if (recvBuffer.length > 4096) {
            console.warn("⚠️ 비정상적 수신 길이 → 초기화");
            started = false;
            recvBuffer = [];
            expectedLength = 0;
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("❌ 수신 중 오류:", err);
        reject(err);
      }
    } finally {
      reader.releaseLock();
    }
  });
};

export const startReadingSnA1 = (
  port: SerialPort,
  timeoutMs: number = 10000,
  serialReceiveRef: RefObject<ReadableStreamDefaultReader<Uint8Array> | null>,
  abortControllerRef: RefObject<AbortController | null>
): Promise<number[]> => {
  return new Promise(async (resolve, reject) => {
    
    const reader = port.readable?.getReader();
    if (!reader) return reject("❌ Reader 생성 실패");

    serialReceiveRef.current = reader;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const STX = 0x02;
    const ETX = 0x03;
    const MIN_FRAME_LENGTH = 12;

    let recvBuffer: number[] = [];
    let started = false;
    let expectedLength = 0;
    let resolved = false;

    const timeout = setTimeout(() => {
      if (!resolved) {
        abortController.abort();
        reader.releaseLock();
        reject("단말기 응답 없음 (Timeout)");
      }
    }, timeoutMs);
    try {
      while (!resolved) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;

        for (const byte of Array.from(value)) {
          if (!started) {
            if (byte === STX) {
              started = true;
              recvBuffer = [STX];
              expectedLength = 0;
            }
            continue;
          }

          recvBuffer.push(byte);

          if (recvBuffer.length >= 4 && expectedLength === 0) {
            expectedLength = 4;
          }

          if (expectedLength > 0 && recvBuffer.length >= expectedLength) {
            const lastByte = recvBuffer[expectedLength - 2];
            if (lastByte !== ETX) {
              console.error("❌ ETX 누락 또는 잘못된 위치");
              started = false;
              recvBuffer = [];
              expectedLength = 0;
              continue;
            }

            const dataPayload = recvBuffer;
            resolved = true;
            clearTimeout(timeout);
            reader.releaseLock();
            resolve(dataPayload);
            break;
          }

          // 너무 긴 garbage 방지
          if (recvBuffer.length > 4096) {
            console.warn("⚠️ 비정상적 수신 길이 → 초기화");
            started = false;
            recvBuffer = [];
            expectedLength = 0;
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("❌ 수신 중 오류:", err);
        reject(err);
      }
    } finally {
      reader.releaseLock();
    }
  });
};


export const startReadingSnA3 = (
  port: SerialPort,
  timeoutMs: number = 10000,
  serialReceiveRef: RefObject<ReadableStreamDefaultReader<Uint8Array> | null>,
  abortControllerRef: RefObject<AbortController | null>
): Promise<number[]> => {
  return new Promise(async (resolve, reject) => {
    const reader = port.readable?.getReader();
    if (!reader) return reject("❌ Reader 생성 실패");

    serialReceiveRef.current = reader;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const STX = 0x02;
    const ETX = 0x03;

    let recvBuffer: number[] = [];
    let started = false;
    let expectedLength = 0;
    let resolved = false;

    const timeout = setTimeout(() => {
      if (!resolved) {
        abortController.abort();
        reader.releaseLock();
        reject("단말기 응답 없음 (Timeout)");
      }
    }, timeoutMs);

    try {
      while (!resolved) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;

        for (const byte of Array.from(value)) {
          if (!started) {
            if (byte === STX) {
              started = true;
              recvBuffer = [STX];
              expectedLength = 0;
            }
            continue;
          }

          recvBuffer.push(byte);

          if (recvBuffer.length === 4 && expectedLength === 0) {
            const lenHi = recvBuffer[2];
            const lenLo = recvBuffer[3];

            if (
              lenHi < 0x30 || lenHi > 0x39 ||
              lenLo < 0x30 || lenLo > 0x39
            ) {
              console.error("❌ 잘못된 Length ASCII 값");
              started = false;
              recvBuffer = [];
              continue;
            }

            const lenVal = (lenHi - 0x30) * 10 + (lenLo - 0x30);
            expectedLength = 1 + 1 + 2 + lenVal + 1 + 1; // STX + 0xA3 + len(2) + data + ETX + LRC
          }

          if (expectedLength > 0 && recvBuffer.length === expectedLength) {
            const etxIndex = expectedLength - 2;
            const lrcIndex = expectedLength - 1;

            if (recvBuffer[etxIndex] !== ETX) {
              console.error("❌ ETX 위치 오류");
              started = false;
              recvBuffer = [];
              expectedLength = 0;
              continue;
            }

            const lrcCalc = recvBuffer
              .slice(0, etxIndex + 1)
              .reduce((acc, val) => acc ^ val, 0);

            const lrcExpected = recvBuffer[lrcIndex];

            if (lrcCalc !== lrcExpected) {
              console.error(`❌ LRC 불일치. 계산=${lrcCalc.toString(16)}, 기대=${lrcExpected.toString(16)}`);
              started = false;
              recvBuffer = [];
              expectedLength = 0;
              continue;
            }

            // ✅ 성공
            const dataPayload = recvBuffer.slice(4, etxIndex); // data만 추출
            resolved = true;
            clearTimeout(timeout);
            reader.releaseLock();
            resolve(dataPayload);
            break;
          }

          if (recvBuffer.length > 2048) {
            console.warn("⚠️ 비정상 수신 길이 초과 → 초기화");
            started = false;
            recvBuffer = [];
            expectedLength = 0;
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("❌ 수신 중 오류:", err);
        reject(err);
      }
    } finally {
      reader.releaseLock();
    }
  });
};


export const startReadingSn3D = (
  port: SerialPort,
  timeoutMs: number = 10000,
  serialReceiveRef: RefObject<ReadableStreamDefaultReader<Uint8Array> | null>,
  abortControllerRef: RefObject<AbortController | null>
): Promise<number[]> => {
  return new Promise(async (resolve, reject) => {
    const reader = port.readable?.getReader();
    if (!reader) return reject("❌ Reader 생성 실패");

    serialReceiveRef.current = reader;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const STX = 0x02;

    let recvBuffer: number[] = [];
    let started = false;
    let expectedLength = 0;
    let resolved = false;

    const timeout = setTimeout(() => {
      if (!resolved) {
        abortController.abort();
        reader.releaseLock();
        reject("단말기 응답 없음 (Timeout)");
      }
    }, timeoutMs);

    try {
      while (!resolved) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;

        for (const byte of Array.from(value)) {
          if (!started) {
            if (byte === STX) {
              started = true;
              recvBuffer = [STX];
              expectedLength = 0;
            }
            continue;
          }

          recvBuffer.push(byte);

          if (recvBuffer.length === 4 && expectedLength === 0) {
            const b0 = recvBuffer[2]; // 첫 번째 길이 바이트
            const b1 = recvBuffer[3]; // 두 번째 길이 바이트
                    
            // 각 니블 추출 (상/하위 4비트)
            const d1 = (b0 >> 4) & 0x0F; // 천의 자리
            const d2 = b0 & 0x0F;        // 백의 자리
            const d3 = (b1 >> 4) & 0x0F; // 십의 자리
            const d4 = b1 & 0x0F;        // 일의 자리
                    
            // BCD 유효성 검사(모든 니블은 0~9)
            if (d1 > 9 || d2 > 9 || d3 > 9 || d4 > 9) {
              console.error("❌ 잘못된 Length BCD 값");
              started = false;
              recvBuffer = [];
              continue;
            }
          
            const lenVal = d1 * 1000 + d2 * 100 + d3 * 10 + d4; // 예: 0x35 0x96 => 3596
          
            expectedLength = 1 + 1 + 2 + lenVal + 1; // STX + 0xA3 + len(2) + data + ETX + LRC
          }

          if (expectedLength > 0 && recvBuffer.length === expectedLength) {
            const lrcIndex = expectedLength -1;
            const lrcCalc = recvBuffer
              .slice(0, lrcIndex)
              .reduce((acc, val) => acc ^ val, 0);

            const lrcExpected = recvBuffer[lrcIndex];

            if (lrcCalc !== lrcExpected) {
              console.error(`❌ LRC 불일치. 계산=${lrcCalc.toString(16)}, 기대=${lrcExpected.toString(16)}`);
              started = false;
              recvBuffer = [];
              expectedLength = 0;
              continue;
            }

            // ✅ 성공
            const dataPayload = recvBuffer.slice(4, lrcIndex); // data만 추출
            resolved = true;
            clearTimeout(timeout);
            reader.releaseLock();
            resolve(dataPayload);
            break;
          }

          if (recvBuffer.length > 2048) {
            console.warn("⚠️ 비정상 수신 길이 초과 → 초기화");
            started = false;
            recvBuffer = [];
            expectedLength = 0;
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("❌ 수신 중 오류:", err);
        reject(err);
      }
    } finally {
      reader.releaseLock();
    }
  });
};
*/

export function parsePemToBinary(pem: string): Uint8Array {
  const cleaned = pem
    .replace("-----BEGIN PUBLIC KEY-----", "")
    .replace("-----END PUBLIC KEY-----", "")
    .replace(/\s+/g, "");
  return Uint8Array.from(atob(cleaned), (c) => c.charCodeAt(0));
}

// 타입/환경 이슈 없이 WebCrypto에 넣기 위한 유틸
type Binary = Uint8Array | ArrayBuffer;

function toArrayBuffer(input: Binary): ArrayBuffer {
  if (input instanceof ArrayBuffer) return input;
  if (
    input.byteOffset === 0 &&
    input.byteLength === input.buffer.byteLength &&
    input.buffer instanceof ArrayBuffer
  ) {
    return input.buffer;
  }
  return input.slice().buffer;
}

export async function importRSAPublicKey(
  derBytes: Uint8Array
): Promise<CryptoKey> {
  return window.crypto.subtle.importKey(
    "spki",
    toArrayBuffer(derBytes),
    { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
    true,
    ["verify"]
  );
}

export async function verifySignature(
  publicKey: CryptoKey,
  message: string,
  signature: Uint8Array
): Promise<boolean> {
  const messageBytes = new TextEncoder().encode(message);
  return window.crypto.subtle.verify(
    { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
    publicKey,
    toArrayBuffer(signature),
    toArrayBuffer(messageBytes)
  );
}

export function getFormattedDate(
  date: Date = new Date(),
  timeZone: string = "Asia/Seoul"
): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);

  const pick = (t: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === t)?.value ?? "";

  const MM = pick("month");
  const dd = pick("day");
  const HH = pick("hour");
  const mm = pick("minute");
  const ss = pick("second");

  return `${HH}:${mm}:${ss}`;
}

const cho = [
  "r",
  "R",
  "s",
  "e",
  "E",
  "f",
  "a",
  "q",
  "Q",
  "t",
  "T",
  "d",
  "w",
  "W",
  "c",
  "z",
  "x",
  "v",
  "g",
];
const jung = [
  "k",
  "o",
  "i",
  "O",
  "j",
  "p",
  "u",
  "P",
  "h",
  "hk",
  "ho",
  "hl",
  "y",
  "n",
  "nj",
  "np",
  "nl",
  "b",
  "m",
  "ml",
  "l",
];
const jong = [
  "",
  "r",
  "R",
  "rt",
  "s",
  "sw",
  "sg",
  "e",
  "f",
  "fr",
  "fa",
  "fq",
  "ft",
  "fx",
  "fv",
  "fg",
  "a",
  "q",
  "qt",
  "t",
  "T",
  "d",
  "w",
  "c",
  "z",
  "x",
  "v",
  "g",
];

const jamoMap: Record<string, string> = {
  ㄱ: "R",
  ㄲ: "R",
  ㄴ: "S",
  ㄷ: "E",
  ㄸ: "E",
  ㄹ: "F",
  ㅁ: "A",
  ㅂ: "Q",
  ㅃ: "Q",
  ㅅ: "T",
  ㅆ: "T",
  ㅇ: "D",
  ㅈ: "W",
  ㅉ: "W",
  ㅊ: "C",
  ㅋ: "Z",
  ㅌ: "X",
  ㅍ: "V",
  ㅎ: "G",
  ㅏ: "K",
  ㅐ: "O",
  ㅑ: "I",
  ㅒ: "O",
  ㅓ: "J",
  ㅔ: "P",
  ㅕ: "U",
  ㅖ: "P",
  ㅗ: "H",
  ㅛ: "Y",
  ㅜ: "N",
  ㅠ: "B",
  ㅡ: "M",
  ㅣ: "L",
};

export const convertEn = (text: string): string => {
  let result = "";

  for (let i = 0; i < text.length; i++) {
    const ch = text.charAt(i);
    const code = ch.charCodeAt(0);

    if (code >= 0xac00 && code <= 0xd7a3) {
      // 완성형 한글 처리
      const base = code - 0xac00;
      const choIdx = Math.floor(base / 588);
      const jungIdx = Math.floor((base % 588) / 28);
      const jongIdx = base % 28;

      result += (cho[choIdx] + jung[jungIdx] + jong[jongIdx]).toUpperCase();
    } else if (jamoMap[ch]) {
      // 자모 단독 입력 처리
      result += jamoMap[ch];
    } else {
      // 숫자, 영문 등은 그대로 대문자화
      result += ch.toUpperCase();
    }
  }

  return result;
};

export const tempPrivateKey = `
-----BEGIN PRIVATE KEY-----
MIIEvAIBADANBgkqhkiG9w0BAQEFAASCBKYwggSiAgEAAoIBAQCzRqsCmIy15M4A
1UCiSDxIxpwtEv2OANct2febKc3R9Y4Ox5K7Ic58knnzSjkGeY5/WXgocDUtbbYO
Asb+DeWR5Iwt9s+vjJOykAi3+GzzNxTe0KJ1xbmPZn+dICvdj8x12UNGHX2JpHUy
54YyWruL7IEL4RofJVIAyoGkPSn6uTKZzcWf+D+ibAo897SgA9/hnIcQJiDXIB6c
DXS2T4tEKkb5wUubOQtegKxQoXhIXVWfKJI9IaKsHsRjUFXgRBvZejsSjUMoqPv4
7UoMrg2dZTDhJzJoeQB/q+ERlbXu+Q8qt3SJ7cNWonGPmr5rykfkuHKPYVmepzN7
sEOXlTIDAgMBAAECggEABRH7FSw2UpChiOF/HffYCKDhWt7+H82DSG8xMwi4IveE
aIOZcURwABLucwSG31mBv7VKki42Os4vB29prFa9RuaFVml0Dz11EehD7twOTMSi
iPiyl40bheQ6uLlntDD0ceLOpcWAvutRSzrED1mEk10Be1IG+JmIG7CegQQf01Ds
UzZMQHnO9xBn3siz/PHICMYNy5PGcDCLpIcwjyA3+xwfJ0iuQ2oGj7EmGPvzftCi
BO8k1fOxLSK2KPlg485fpkEpc9NgD0VzAEgYIXkhftjUpWiu4JeAB1jX3SAYP74l
ErQfbkDFICf5pRHEhzuJ0xDfZcxES3b2Gib12R/NzQKBgQD17A8PsGwT3cB+kPMa
uesEqQ0i7Iq3LjJ+84euCoP9uPXTA2AP3X9uUbsVvaW1O0GH3kQVRmtPppuhGDyA
25rnZSBHma1uVzArrGaUvzkGaSgSIWx8JyAlcCAq27Ts1HmrgwgPgiWKPiMq7y22
Sbvu/+s65HcbJOMRe76ayi1dnwKBgQC6n27Qn6NzrQA4biGjId1Ur/M9hGrcyQrt
eiNstplVJYlbzhKRtLUgyJUAAheGufHFHKYXS9uHdfR9u/ucgLkGg0tINW2OJrBe
QFvVwDJAu54USbtc7LLA1u945Ayns510hreYFPHQIwyY3oWIuFF22ctJ8dC/pvbQ
AvlTKHkJHQKBgBJFQHU08UKklfmRQU56A5jsClrO4htr5jqCU1E93MKL7E+KD6f+
F8qVbeXWmioVxe+XNs2x1qUrRqK4Tz5vklmgrL5FD5qEBcMo4tIoRs9QHrcCHrI2
oe/GHc2xVwbFXmi8qmukiq9zoF8/N9E0Ny8xekN819B/dBNHc0jCvo4XAoGAMrof
jwm1jj3aL3yQHB2aYeipv3D88yKam851TpmbuC9J1PCAz6ZFDbI3/haTeoWUmDWf
V/nApGOFn5+3VuUOZhzOG8SaEOenTRdFXyatAjd9UppwGWQlepFx6nRilDsYc5Ii
2W3oBOFiDTN/LJidXCb7QRA/47hookYMq3P24mkCgYATgAawh6CMGZH3QtrAjp3C
PzAgDSDmuIesLCYgYEhCfRV8Vs0UJKr1rhmvSMtcd6pQqyUWg/TkofIMhLSlo9v8
Qitik/d85mVUDDxjsRnSZ9uQgrSI1sd9JdIVay56YRwt7mHXx8iPF4GkNygD0DLQ
rC0magisRCfvnjdNtmjnnA==
-----END PRIVATE KEY-----
`;

export const tempPublicKey = `
-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAs0arApiMteTOANVAokg8
SMacLRL9jgDXLdn3mynN0fWODseSuyHOfJJ580o5BnmOf1l4KHA1LW22DgLG/g3l
keSMLfbPr4yTspAIt/hs8zcU3tCidcW5j2Z/nSAr3Y/MddlDRh19iaR1MueGMlq7
i+yBC+EaHyVSAMqBpD0p+rkymc3Fn/g/omwKPPe0oAPf4ZyHECYg1yAenA10tk+L
RCpG+cFLmzkLXoCsUKF4SF1VnyiSPSGirB7EY1BV4EQb2Xo7Eo1DKKj7+O1KDK4N
nWUw4ScyaHkAf6vhEZW17vkPKrd0ie3DVqJxj5q+a8pH5Lhyj2FZnqcze7BDl5Uy
AwIDAQAB
-----END PUBLIC KEY-----
`;

export const lpad = (value: any, length = 6, padChar = "0") => {
  const str = String(value);
  const neededLength = length - str.length;
  const padding = neededLength > 0 ? padChar.repeat(neededLength) : "";
  return padding + str;
};

type LogEventType =
  | "PAGE_VIEW"
  | "BUTTON_CLICK"
  | "FORM_SUBMIT"
  | "LOGIN"
  | "LOGOUT"
  | "ACCESS"
  | "ACCESS_DENIED"
  | "CREATE"
  | "UPDATE"
  | "DELETE"
  | "IMPORT"
  | "EXPORT"
  | "CONNECT"
  | "INPUT"
  | "KEY_INJECT"
  | "API_CALL";
export const LogEventTypeOptions: { label: string; value: string }[] = [
  { label: "전체", value: "" },
  { label: "페이지 조회", value: "PAGE_VIEW" },
  { label: "버튼 클릭", value: "BUTTON_CLICK" },
  { label: "폼 제출", value: "FORM_SUBMIT" },
  { label: "로그인", value: "LOGIN" },
  { label: "로그아웃", value: "LOGOUT" },
  { label: "접근", value: "ACCESS" },
  { label: "접근 거부", value: "ACCESS_DENIED" },
  { label: "생성", value: "CREATE" },
  { label: "수정", value: "UPDATE" },
  { label: "삭제", value: "DELETE" },
  { label: "가져오기", value: "IMPORT" },
  { label: "내보내기", value: "EXPORT" },
  { label: "연결", value: "CONNECT" },
  { label: "입력", value: "INPUT" },
  { label: "키 주입", value: "KEY_INJECT" },
  { label: "API 호출", value: "API_CALL" },
];

const LogEventTypeMap: { [key: string]: string } = {
  PAGE_VIEW: "페이지 조회",
  BUTTON_CLICK: "버튼 클릭",
  FORM_SUBMIT: "폼 제출",
  LOGIN: "로그인",
  LOGOUT: "로그아웃",
  ACCESS: "접근",
  ACCESS_DENIED: "접근 거부",
  CREATE: "생성",
  UPDATE: "수정",
  DELETE: "삭제",
  IMPORT: "가져오기",
  EXPORT: "내보내기",
  CONNECT: "연결",
  INPUT: "입력",
  KEY_INJECT: "키 주입",
  API_CALL: "API 호출",
};

export const getEventTypeLabel = (eventType: string) => {
  return LogEventTypeMap[eventType] || eventType;
};

interface ActionLog {
  eventType: LogEventType;
  title: string;
  content?: unknown;
  serialNo?: string;
}

export interface DiagnosticError {
  errorCode?: string;
  errorType?: string;
  errorMessage?: string;
  errorDetail?: string;
}

/**
 * 서버 에러 응답 및 비동기 작업 에러를 파싱하여
 * - UI 토스트용 [ERR-코드: 타입] 메시지
 * - 로그 관리(action_log) 적재용 상세 진단 텍스트
 * 로 분리 변환하는 헬퍼 함수
 */
export const formatDiagnosticError = (
  err: any,
  fallbackMsg: string = "작업에 실패했습니다."
): { displayMsg: string; logContent: string } => {
  const respData = err?.response?.data;
  const dataObj =
    respData?.data ||
    (typeof respData === "object" ? respData : null) ||
    (typeof err === "object" ? err : null);

  const errorCode = dataObj?.errorCode || respData?.code || "9999";
  const errorType =
    dataObj?.errorType ||
    respData?.errorType ||
    (err instanceof Error && err.name !== "Error" ? err.name : "SystemError");
  const baseMsg =
    dataObj?.errorMessage ||
    respData?.msg ||
    respData?.message ||
    err?.message ||
    fallbackMsg;
  const errorDetail = dataObj?.errorDetail || respData?.errorDetail || "";

  const displayMsg = `[ERR-${errorCode}: ${errorType}] ${baseMsg}`;
  const logContent = [
    `[에러 코드: ERR-${errorCode}]`,
    `[예외 유형: ${errorType}]`,
    `[메시지: ${baseMsg}]`,
    errorDetail ? `\n[백엔드 상세 시스템 에러 / Stack Trace]\n${errorDetail}` : "",
  ].filter(Boolean).join("\n");

  return { displayMsg, logContent };
};

export const setActionLog = async (data: ActionLog) => {
  try {
    const rawMessage =
      data.content == null
        ? ""
        : typeof data.content === "string"
        ? data.content
        : data.content instanceof Error
        ? (data.content.message || String(data.content))
        : (() => {
            try {
              return JSON.stringify(data.content);
            } catch {
              return String(data.content);
            }
          })();

    // DB message 컬럼(varchar 2000) 초과 방지
    const message =
      rawMessage.length > 1900
        ? rawMessage.substring(0, 1890) + "\n...(일부 생략됨)"
        : rawMessage;

    const logData: any = {
      menuUrl: window.location.pathname,
      eventType: data.eventType,
      action: data.title,
      message,
    };

    if (data.serialNo) {
      logData.serialNo = data.serialNo;
    }

    console.log("ActionLog 전송 데이터:", logData); // 디버깅용 로그

    await axiosInstance.post("/api/v1/logs/action", logData);
  } catch (err: any) {
    console.error(err);
  }
};

export const randomString = (length: number): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const bytesToHexString = (bytes: Uint8Array | number[]): string => {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

export const hexStringToBytes = (hex: string) => {
  if (hex.length % 2 !== 0) {
    throw new Error("❌ Hex 문자열의 길이는 짝수여야 합니다.");
  }

  if (!/^[0-9a-fA-F]+$/.test(hex)) {
    throw new Error("❌ 유효하지 않은 hex 문자열입니다.");
  }

  const result = new Uint8Array(hex.length / 2);

  for (let i = 0; i < hex.length; i += 2) {
    result[i / 2] = parseInt(hex.substr(i, 2), 16);
  }

  return result;
};

export const stripCtrl = (s: string) =>
  s.replace(/[\u0000-\u001F\u007F]/g, "").trim();

export const containsHangul = (text: string) => {
  return /[\u3131-\u318E\uAC00-\uD7A3]/.test(text);
};

export const xorChecksumByte = (bytes: Uint8Array): number => {
  if (bytes.length < 2) return 0;

  let xor = bytes[0];
  for (let i = 1; i < bytes.length - 1; i++) {
    xor ^= bytes[i];
  }

  return xor; // byte 값
};

export const boardDateFormat = (dateStr: string): string => {
  const date = dayjs(dateStr);
  const today = dayjs();

  if (date.isSame(today, "day")) {
    // 오늘이면 hh:mm:ss
    return date.format("HH:mm:ss");
  } else {
    // 오늘 아니면 yyyy-MM-dd hh:mm:ss
    return date.format("YYYY-MM-DD HH:mm:ss");
  }
};

/*

//const tempText = "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";

  const handleSend = async () => {
    try {
      const port = serialPortRef.current;
      if (!port || !isSerialPortOpen) throw new Error("포트가 열려있지 않습니다");

      const writer = port.writable?.getWriter();
      if (!writer) throw new Error("쓰기 스트림을 가져올 수 없습니다");

      //await writer.write(hexStringToBytes(tempText));
      //await sendBlockJs(writer, textParam);
      

  const sendBytes = new Uint8Array(2048);
  const pemBytes = new TextEncoder().encode("123");
  sendBytes.set(pemBytes); // insert from index 1

      //await sendBlockJs(writer)


      

      writer.releaseLock();
      setWriteLogs(prev => [...prev, textParam]);

    } catch (err: any) {
      alert("❌ 전송 오류: " + err.message);
    }
  };
*/

/*
async function sendBlockJs_old(writer: WritableStreamDefaultWriter<Uint8Array>, pemText: string) {
  const FIXED_LENGTH = 2049;
  const byCmd = 0xF9;
let msgSeqNo = 0
  // 1. Build the data buffer (2049 bytes)
  const data = new Uint8Array(FIXED_LENGTH);
  data[0] = 0xF3; // first byte always 0xF3
  const pemBytes = new TextEncoder().encode(pemText);

  // copy PEM into [1..]
  const copyLength = Math.min(pemBytes.length, FIXED_LENGTH - 1);
  data.set(pemBytes.slice(0, copyLength), 1); // insert from index 1
  // rest already zero-padded because new Uint8Array initializes to 0

  // 2. Build full message
  const OutMesg = new Uint8Array(4096);
  const TempMesg = new Uint8Array(4096);

  let iIdx = 0;
  OutMesg[iIdx++] = 0x02;                      // STX
  OutMesg[iIdx++] = msgSeqNo++ & 0xFF;         // Seq No
  OutMesg[iIdx++] = 0x0B;                      // Sender Index H
  OutMesg[iIdx++] = 0x01;                      // Sender Index L
  OutMesg[iIdx++] = 0x0E;                      // Receiver Index H
  OutMesg[iIdx++] = 0x01;                      // Receiver Index L
  OutMesg[iIdx++] = byCmd;                     // Instruction Code

  OutMesg[iIdx++] = (FIXED_LENGTH >> 8) & 0xFF; // Message Length H (0x08)
  OutMesg[iIdx++] = FIXED_LENGTH & 0xFF;        // Message Length L (0x01)

  // insert 2049 bytes of data
  OutMesg.set(data, iIdx);
  iIdx += FIXED_LENGTH;

  // 3. Copy for CRC16 from OutMesg[1] ~ [8+Length]
  for (let i = 0; i < 8 + FIXED_LENGTH; i++) {
    TempMesg[i] = OutMesg[1 + i];
  }

  // CRC16 using your lookup-based function
  const crc16 = calcCrc16(Array.from(TempMesg.slice(0, 8 + FIXED_LENGTH)));

  OutMesg[iIdx++] = (crc16 >> 8) & 0xFF;
  OutMesg[iIdx++] = crc16 & 0xFF;
  OutMesg[iIdx++] = 0x03; // ETX

  const finalPacket = OutMesg.slice(0, iIdx);
  await writer.write(finalPacket);

  console.log(`[sendBlockJs] F9 packet sent (${iIdx} bytes)`);
}
*/

/*
const startReading = (port: SerialPort) => {
    const decoder = new TextDecoder();
    const reader = port.readable?.getReader();
    if (!reader) return;

    serialReceiveRef.current = reader;
    abortControllerRef.current = new AbortController();

    let buffer = "";

    const readLoop = async () => {
    const STX = 0x02;
    const ETX = 0x03;
    const MIN_FRAME_LENGTH = 12;

    let recvBuffer: number[] = [];
    let started = false;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        
        // Uint8Array -> Array<number>
        for (const byte of Array.from(value)) {
          if (!started) {
            if (byte === STX) {
              started = true;
              recvBuffer = [STX];
            }
            continue; // skip garbage
          } else {
            recvBuffer.push(byte);

            // 최소 길이 만족 시 길이 확인
            if (recvBuffer.length >= 9) {
              const lenHi = recvBuffer[7];
              const lenLo = recvBuffer[8];
              const dataLen = (lenHi << 8) | lenLo;
              const totalLen = dataLen + 12;

              // 전체 프레임 수신 완료 여부 확인
              if (recvBuffer.length === totalLen) {
                const isETX = recvBuffer[recvBuffer.length - 1] === ETX;
                if (!isETX) {
                  console.error("❌ ETX 누락 또는 오류");
                  started = false;
                  recvBuffer = [];
                  continue;
                }

                // CRC16 검증
                const msgForCrc = recvBuffer.slice(1, 1 + 8 + dataLen); // STX 제외, CRC 포함 안 함
                const expectedCrcHi = recvBuffer[8 + dataLen + 1];
                const expectedCrcLo = recvBuffer[8 + dataLen + 2];

                const crc16 = calcCrc16(msgForCrc); // ↓ 아래 함수 참고

                if (
                  expectedCrcHi !== (crc16 >> 8) ||
                  expectedCrcLo !== (crc16 & 0xff)
                ) {
                  console.error("❌ CRC 오류");
                  started = false;
                  recvBuffer = [];
                  continue;
                }

                // ✅ 유효한 프레임 수신 완료
                const dataPayload = recvBuffer.slice(9, 9 + dataLen);
                const hexStr = dataPayload.map(b => b.toString(16).padStart(2, "0")).join(" ");
                setLogs(prev => [...prev, `✅ 수신: ${hexStr}`]);

                // 다음 프레임 대기
                started = false;
                recvBuffer = [];
              } else if (recvBuffer.length > totalLen) {
                // 길이 초과 → 파싱 실패
                console.warn("⚠️ 예상보다 긴 수신 → 초기화");
                started = false;
                recvBuffer = [];
              }
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("❌ 수신 중 오류:", err);
      }
    } finally {
      reader.releaseLock();
    }
  };
    readLoop();
  };
*/
